/*
 * Size chart: shows measurements for the selected size, and opens the full
 * table in a modal.
 *
 * The chart is keyed by the blank's size labels (S, M, L/XL, ...), which have
 * to be matched against Shopify variant option values. Those are entered by
 * hand in the admin and drift — "S/M" vs "s / m", curly vs straight quotes in
 * '30″×40″'. Matching is therefore normalised rather than exact, and falls
 * back to hiding the inline line (never to showing a wrong measurement).
 */
class SizeChart extends HTMLElement {
  connectedCallback() {
    try {
      this.data = JSON.parse(this.dataset.table || '{}');
    } catch (e) {
      this.data = {};
    }
    this.unit = this.dataset.unit || 'cm';
    this.selectedEl = this.querySelector('[data-size-chart-selected]');
    this.dialog = this.querySelector('[data-size-chart-dialog]');

    this.querySelector('[data-size-chart-open]')?.addEventListener('click', () => this.open());
    this.querySelector('[data-size-chart-close]')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });

    /* Listen on document, not on the variant picker.
     *
     * Dawn re-renders the product form through the Section Rendering API on
     * every variant change, swapping <variant-selects> for a fresh node. A
     * listener bound directly to that element survives exactly one change and
     * is then orphaned on a detached node — which looks like "it updates once
     * and then stops". `change` bubbles, so delegating from document keeps
     * working across re-renders. */
    this.onChange = (event) => {
      if (!event.target?.closest) return;
      // ignore changes from a different product block on the same page
      if (this.closest('.product') && !this.closest('.product').contains(event.target)) return;
      // let Dawn finish swapping nodes before reading the checked input
      setTimeout(() => this.update(), 0);
    };
    document.addEventListener('change', this.onChange);
    document.addEventListener('variant:change', this.onChange);

    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onChange);
    document.removeEventListener('variant:change', this.onChange);
  }

  open() {
    if (!this.dialog) return;
    this.highlight();
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
  }

  /* Lowercase, strip whitespace, and fold the many dash/quote/slash variants
     merchants type so "L / XL" and "l-xl" both reach the "L/XL" row. */
  static normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[‘’“”]/g, '"')
      .replace(/[′″]/g, '"')
      .replace(/[–—]/g, '-')
      .replace(/[\s._-]+/g, '')
      .replace(/[\/\\]/g, '/');
  }

  /* The size option is whichever selected option value matches a chart row —
     safer than assuming the option is named "Size", which breaks in pt/es. */
  selectedSize() {
    const rows = this.data.rows || {};
    const keys = Object.keys(rows);
    if (!keys.length) return null;

    const lookup = new Map(keys.map((k) => [SizeChart.normalise(k), k]));
    const candidates = [];

    this.closest('.product')
      ?.querySelectorAll('input[type="radio"]:checked, select option:checked')
      .forEach((el) => candidates.push(el.value));

    for (const value of candidates) {
      const hit = lookup.get(SizeChart.normalise(value));
      if (hit) return hit;
    }
    return null;
  }

  update() {
    if (!this.selectedEl) return;
    const size = this.selectedSize();
    const cols = this.data.columns || [];
    const row = size ? this.data.rows[size] : null;

    if (!row) {
      this.selectedEl.hidden = true;
      this.selectedEl.textContent = '';
      return;
    }

    const parts = cols.map((c, i) => `${c} ${row[i]}${this.unit}`);
    this.selectedEl.textContent = `${size} — ${parts.join(' · ')}`;
    this.selectedEl.hidden = false;
    this.highlight();
  }

  highlight() {
    const size = this.selectedSize();
    this.querySelectorAll('[data-size-chart-row]').forEach((tr) => {
      tr.classList.toggle('is-selected', tr.dataset.sizeChartRow === size);
    });
  }
}

if (!customElements.get('size-chart')) {
  customElements.define('size-chart', SizeChart);
}
